// src/utils/image-upload.js
import request from '@/request'
import config from '@/config'
import { ElMessage } from 'element-plus'

/**
 * 图片上传工具类
 * 功能：格式校验、大小限制、本地预览、服务器上传、资源清理
 */
export const ImageUpload = {
  // 基础配置（统一管理，便于修改）
  config: {
    allowedTypes: ['image/jpeg', 'image/png', 'image/jpg'], // 允许的图片格式
    maxSize: 2 * 1024 * 1024, // 最大尺寸（2MB）
    uploadUrl: `${config.baseApiUrl}/upload/uploadWebImage` // 上传接口
  },

  /**
   * 1. 图片校验（格式 + 大小）
   * @param {File} file - 图片文件
   * @returns {boolean} 校验通过返回true，失败返回false
   */
  validate(file) {
    if (!file) {
      ElMessage.error('请选择图片文件')
      return false
    }

    // 格式校验
    const isTypeValid = this.config.allowedTypes.includes(file.type)
    if (!isTypeValid) {
      const typeText = this.config.allowedTypes.map(t => t.split('/')[1].toUpperCase()).join('/')
      ElMessage.error(`仅支持${typeText}格式图片`)
      return false
    }

    // 大小校验
    const isSizeValid = file.size <= this.config.maxSize
    if (!isSizeValid) {
      ElMessage.error(`图片大小不能超过${this.config.maxSize / 1024 / 1024}MB`)
      return false
    }

    return true
  },

  /**
   * 2. 生成本地预览URL（仅前端预览，不发请求）
   * @param {File} file - 图片文件
   * @returns {string} 预览URL
   */
  createPreviewUrl(file) {
    if (!file) return ''
    return URL.createObjectURL(file)
  },

  /**
   * 3. 清理预览资源（避免内存泄漏）
   * @param {string} previewUrl - 预览URL
   */
  revokePreviewUrl(previewUrl) {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
  },

  /**
   * 4. 上传图片到服务器（核心方法）
   * @param {File} file - 图片文件
   * @param {Object} options - 上传配置
   * @param {string} options.type - 图片分类（如topic/，后端可能需要）
   * @param {Function} options.onLoading - 加载状态回调（true=加载中，false=加载完成）
   * @returns {Promise<string>} 成功返回图片服务器URL，失败抛出错误
   */
  async upload(file, options = {}) {
    // 先校验图片
    const isValid = this.validate(file)
    if (!isValid) throw new Error('图片校验失败')

    // 触发加载中回调
    if (options.onLoading && typeof options.onLoading === 'function') {
      options.onLoading(true)
    }

    try {
      // 构建FormData（后端要求multipart/form-data格式）
      const formData = new FormData()
      formData.append('file', file)
      formData.append('type', options.type || 'topic/') // 默认为专题图片分类

      // 发送上传请求
      const res = await request.post(this.config.uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      // 校验响应
      if (res.data?.success && res.data?.url) {
        ElMessage.success('图片上传成功')
        return res.data.url
      } else {
        throw new Error(res.data?.msg || '上传失败，未返回图片URL')
      }
    } catch (error) {
      ElMessage.error(error.message || '图片上传异常')
      throw error // 抛出错误，让组件处理后续逻辑
    } finally {
      // 触发加载完成回调
      if (options.onLoading && typeof options.onLoading === 'function') {
        options.onLoading(false)
      }
    }
  }
}

// 全局挂载方法（可选，便于Vue组件直接通过this.$imageUpload调用）
export const install = (app) => {
  app.config.globalProperties.$imageUpload = ImageUpload
}

export default { ImageUpload, install }